import { useMemo } from 'react'

import {
  CreateOrganizationMutation,
  CreateOrganizationMutationVariables,
  GeneratePaymentusTokenMutation,
  GeneratePaymentusTokenMutationVariables,
  GetOrganizationAISettingsQuery,
  GetOrganizationBySlugQuery,
  GetOrganizationBySlugQueryVariables,
  GetOrganizationEntityLayersQuery,
  GetOrganizationForAIChatWindowQuery,
  GetOrganizationForAIChatWindowQueryVariables,
  GetOrganizationForInsightsInspectionsQuery,
  GetOrganizationGovWellStaffManagementQuery,
  GetOrganizationGovWellStaffManagementQueryVariables,
  GetOrganizationsGovWellStaffManagementQuery,
  GisSetupQuery,
  OrganizationForAddressesAndParcelsPageQuery,
  OrganizationForAddressesAndParcelsPageQueryVariables,
  OrganizationRecordSettingsQuery,
  OrganizationToggleShouldExposeViolationsMutation,
  OrganizationToggleShouldExposeViolationsMutationVariables,
  PaymentProviderQuery,
  ToggleAIEnabledForOrganizationMutation,
  ToggleAIEnabledForOrganizationMutationVariables,
  ToggleAreAddressesAndParcelsPagesEnabledMutation,
  ToggleAreAddressesAndParcelsPagesEnabledMutationVariables,
  ToggleCodeEnforcementMutation,
  ToggleCodeEnforcementMutationVariables,
  ToggleCodeEnforcementVisibilityMutation,
  ToggleCodeEnforcementVisibilityMutationVariables,
  ToggleExternalCardReaderEnabledForOrganizationMutation,
  ToggleExternalCardReaderEnabledForOrganizationMutationVariables,
  ToggleDisallowAnonymousViolationsForOrganizationMutation,
  ToggleDisallowAnonymousViolationsForOrganizationMutationVariables,
  ToggleOrganizationGISSetupMutation,
  ToggleOrganizationGISSetupMutationVariables,
  ToggleShouldShowPropertyOwnerOnInvoiceReceiptMutation,
  ToggleShouldShowPropertyOwnerOnInvoiceReceiptMutationVariables,
  ToggleShouldSendPlansSetsInEmailMutation,
  ToggleShouldSendPlansSetsInEmailMutationVariables,
  ToggleShouldUseIdentifierPrefixForViolationsMutation,
  ToggleShouldUseIdentifierPrefixForViolationsMutationVariables,
  UpdateAIChatbotNoSubstantiveAnswerMessageMutation,
  UpdateAIChatbotNoSubstantiveAnswerMessageMutationVariables,
  UpdateAddressesAndParcelsPageHeaderTextMutation,
  UpdateAddressesAndParcelsPageHeaderTextMutationVariables,
  UpdateLogoUrlMutation,
  UpdateLogoUrlMutationVariables,
  UpdateOrganizationAICommunityAssistantTierMutation,
  UpdateOrganizationAICommunityAssistantTierMutationVariables,
  UpdateOrganizationContactInfoMutation,
  UpdateOrganizationContactInfoMutationVariables,
  UpdateOrganizationMutation,
  UpdateOrganizationMutationVariables,
  UpdateOrganizationTrialEndDateMutation,
  UpdateOrganizationTrialEndDateMutationVariables,
  GetOrganizationByRecordTypeIdQuery,
  GetOrganizationByRecordTypeIdQueryVariables,
  GetOrganizationByRecordUuidQuery,
  GetOrganizationByRecordUuidQueryVariables,
  GetOrganizationByRecordTemplateUuidQuery,
  GetOrganizationByRecordTemplateUuidQueryVariables,
  IsOrganizationSlugAvailableQuery,
  IsOrganizationSlugAvailableQueryVariables,
  State,
  ToggleShouldExposeInspectionAvailabilityInformationToPublicMutation,
  ToggleShouldExposeInspectionAvailabilityInformationToPublicMutationVariables,
} from 'types/graphql'

import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { QueryKey, useMutation, useQuery, UseQueryOptions } from 'src/utils/queryClient'

const TogglePrefix = gql`
  mutation ToggleShouldUseIdentifierPrefixForViolationsMutation(
    $id: Int!
    $shouldUseIdentifierPrefixForViolations: Boolean!
  ) {
    toggleShouldUseIdentifierPrefixForViolationsForOrganization(
      id: $id
      shouldUseIdentifierPrefixForViolations: $shouldUseIdentifierPrefixForViolations
    ) {
      id
    }
  }
`

export const useToggleShouldUseIdentifierPrefixForViolationsMutation = () => {
  return useMutation<
    ToggleShouldUseIdentifierPrefixForViolationsMutation,
    ToggleShouldUseIdentifierPrefixForViolationsMutationVariables
  >({
    mutationDocument: TogglePrefix,
  })
}

const ToggleDisallowAnonymous = gql`
  mutation ToggleDisallowAnonymousViolationsForOrganizationMutation(
    $id: Int!
    $disallowAnonymousViolations: Boolean!
  ) {
    toggleDisallowAnonymousViolationsForOrganization(
      id: $id
      disallowAnonymousViolations: $disallowAnonymousViolations
    ) {
      id
    }
  }
`

export const useToggleDisallowAnonymousViolationsForOrganizationMutation = () => {
  return useMutation<
    ToggleDisallowAnonymousViolationsForOrganizationMutation,
    ToggleDisallowAnonymousViolationsForOrganizationMutationVariables
  >({ mutationDocument: ToggleDisallowAnonymous })
}

const GetOrganizationsGovWellStaffManagement = gql`
  query GetOrganizationsGovWellStaffManagementQuery {
    organizations {
      ...AdminOrgFragment
    }
  }
  fragment AdminOrgFragment on Organization {
    id
    city
    displayName
    slug
    state
    users {
      id
    }
    timeZone
    hasCodeEnforcementModule
    recordTypes {
      id
    }
  }
`
export const useGetOrganizationsGovWellStaffManagementQuery = () => {
  return useQuery<GetOrganizationsGovWellStaffManagementQuery>({
    queryKey: [QueryKey.GetOrganizationsGovWellStaffManagement],
    queryDocument: GetOrganizationsGovWellStaffManagement,
  })
}

const GetOrganizationGovWellStaffManagement = gql`
  query GetOrganizationGovWellStaffManagementQuery($slug: String!) {
    organizationBySlug(slug: $slug) {
      ...OrganizationFragmentNoUsers
      shouldSendPlansSetsInEmail
      shouldExposeViolations
      shouldUseIdentifierPrefixForViolations
      disallowAnonymousViolations
      paymentProviderFormatted
      isACHEnabled
      isExternalCardReaderEnabled
      shouldShowPropertyOwnerOnInvoiceReceipt
      addressesAndParcelsPageHeaderText
      userOrganizationMemberships {
        ...UserOrganizationMembershipWithUserFragment
      }
      finixMerchants {
        ...OrganizationFinixMerchantsFragment
      }
      recordSettings {
        shouldOrganizationAbsorbServiceFee
        inspectionSchedulingShouldShowTimesToApplicants
      }
    }
  }
`
export const useGetOrganizationGovWellStaffManagementQuery = (slug: string) => {
  return useQuery<
    GetOrganizationGovWellStaffManagementQuery,
    GetOrganizationGovWellStaffManagementQueryVariables
  >({
    queryKey: [QueryKey.GetOrganizationGovWellStaffManagement, slug],
    queryDocument: GetOrganizationGovWellStaffManagement,
    variables: { slug: decodeURIComponent(slug) },
  })
}

const UpdateLogo = gql`
  mutation UpdateLogoUrlMutation($organizationId: Int!, $path: String!) {
    updateLogoUrl(id: $organizationId, path: $path) {
      id
    }
  }
`
export const useUpdateLogoUrlMutation = () => {
  return useMutation<UpdateLogoUrlMutation, UpdateLogoUrlMutationVariables>({
    mutationDocument: UpdateLogo,
  })
}

const ToggleShouldSendPlansSetsInEmail = gql`
  mutation ToggleShouldSendPlansSetsInEmailMutation(
    $id: Int!
    $shouldSendPlansSetsInEmail: Boolean!
  ) {
    toggleShouldSendPlansSetsInEmail(
      id: $id
      shouldSendPlansSetsInEmail: $shouldSendPlansSetsInEmail
    ) {
      id
    }
  }
`
export const useToggleShouldSendPlansSetsInEmailMutation = () => {
  return useMutation<
    ToggleShouldSendPlansSetsInEmailMutation,
    ToggleShouldSendPlansSetsInEmailMutationVariables
  >({
    mutationDocument: ToggleShouldSendPlansSetsInEmail,
  })
}

const ToggleShouldExposeInspectionAvailabilityInformationToPublic = gql`
  mutation ToggleShouldExposeInspectionAvailabilityInformationToPublicMutation(
    $id: Int!
    $shouldExposeInspectionAvailabilityInformationToPublic: Boolean!
  ) {
    toggleShouldExposeInspectionAvailabilityInformationToPublic(
      id: $id
      shouldExposeInspectionAvailabilityInformationToPublic: $shouldExposeInspectionAvailabilityInformationToPublic
    ) {
      id
      shouldExposeInspectionAvailabilityInformationToPublic
    }
  }
`

export const useToggleShouldExposeInspectionAvailabilityInformationToPublicMutation = () => {
  return useMutation<
    ToggleShouldExposeInspectionAvailabilityInformationToPublicMutation,
    ToggleShouldExposeInspectionAvailabilityInformationToPublicMutationVariables
  >({
    mutationDocument: ToggleShouldExposeInspectionAvailabilityInformationToPublic,
  })
}

const OrganizationToggleShouldExposeViolations = gql`
  mutation OrganizationToggleShouldExposeViolationsMutation(
    $id: Int!
    $shouldExposeViolations: Boolean!
  ) {
    organizationToggleShouldExposeViolations(
      id: $id
      shouldExposeViolations: $shouldExposeViolations
    ) {
      id
    }
  }
`
export const useOrganizationToggleShouldExposeViolationsMutation = () => {
  return useMutation<
    OrganizationToggleShouldExposeViolationsMutation,
    OrganizationToggleShouldExposeViolationsMutationVariables
  >({
    mutationDocument: OrganizationToggleShouldExposeViolations,
  })
}

const UpdateOrganization = gql`
  mutation UpdateOrganizationMutation($input: UpdateOrganizationInput!) {
    updateOrganization(input: $input) {
      id
    }
  }
`
export const useUpdateOrganizationMutation = () => {
  return useMutation<UpdateOrganizationMutation, UpdateOrganizationMutationVariables>({
    mutationDocument: UpdateOrganization,
  })
}

const ToggleCodeEnforcement = gql`
  mutation ToggleCodeEnforcementMutation($id: Int!, $hasCodeEnforcementModule: Boolean!) {
    toggleCodeEnforcement(id: $id, hasCodeEnforcementModule: $hasCodeEnforcementModule) {
      id
    }
  }
`
export const useToggleCodeEnforcementMutation = () => {
  return useMutation<ToggleCodeEnforcementMutation, ToggleCodeEnforcementMutationVariables>({
    mutationDocument: ToggleCodeEnforcement,
  })
}

const ToggleCodeEnforcementVisibility = gql`
  mutation ToggleCodeEnforcementVisibilityMutation(
    $id: Int!
    $showCodeEnforcementModule: Boolean!
  ) {
    toggleCodeEnforcementVisibility(
      id: $id
      showCodeEnforcementModule: $showCodeEnforcementModule
    ) {
      id
    }
  }
`
export const useToggleCodeEnforcementVisibilityMutation = () => {
  return useMutation<
    ToggleCodeEnforcementVisibilityMutation,
    ToggleCodeEnforcementVisibilityMutationVariables
  >({ mutationDocument: ToggleCodeEnforcementVisibility })
}

const Create = gql`
  mutation CreateOrganizationMutation($input: CreateOrganizationInput!) {
    createOrganization(input: $input) {
      id
    }
  }
`
export const useCreateOrganizationMutation = () => {
  return useMutation<CreateOrganizationMutation, CreateOrganizationMutationVariables>({
    mutationDocument: Create,
  })
}

const IsOrganizationSlugAvailable = gql`
  query IsOrganizationSlugAvailableQuery($slug: String!, $state: State!) {
    isOrganizationSlugAvailable(slug: $slug, state: $state)
  }
`
export const useIsOrganizationSlugAvailableQuery = (args: {
  slug: string
  state: State | undefined
  enabled?: boolean
}) => {
  return useQuery<IsOrganizationSlugAvailableQuery, IsOrganizationSlugAvailableQueryVariables>({
    queryKey: [QueryKey.OrganizationSlugAvailable, args.slug, args.state],
    queryDocument: IsOrganizationSlugAvailable,
    variables: { slug: args.slug, state: args.state as State },
    // Min slug length is 4 (1+ char name + `-XX`); below that we never call.
    enabled: !!args.enabled && !!args.state && args.slug.length >= 4,
  })
}

const PaymentProviderFormattedQuery = gql`
  query PaymentProviderQuery($organizationId: Int!) {
    organization(id: $organizationId) {
      paymentProviderFormatted
    }
  }
`
export const usePaymentProviderFormatted = () => {
  const organizationId = useMyOrgId()
  return useQuery<PaymentProviderQuery, { organizationId: number }>({
    queryKey: [QueryKey.OrganizationPaymentProviderFormatted, organizationId],
    queryDocument: PaymentProviderFormattedQuery,
    variables: { organizationId: organizationId ?? -1 },
    enabled: !!organizationId,
  })
}

const OrgRecordSettings = gql`
  query OrganizationRecordSettingsQuery($organizationId: Int!) {
    organization(id: $organizationId) {
      recordSettings {
        inspectionSchedulingShouldShowTimesToApplicants
      }
    }
  }
`
export const useOrganizationRecordSettingsQuery = () => {
  const organizationId = useMyOrgId()
  const { data, ...rest } = useQuery<OrganizationRecordSettingsQuery, { organizationId: number }>({
    queryKey: [QueryKey.OrganizationRecordSettings, organizationId],
    queryDocument: OrgRecordSettings,
    variables: { organizationId: organizationId ?? -1 },
    enabled: !!organizationId,
  })
  return {
    inspectionSchedulingShouldShowTimesToApplicants:
      data?.organization?.recordSettings?.inspectionSchedulingShouldShowTimesToApplicants ?? false,
    ...rest,
  }
}

const ToggleAIEnabledForOrganization = gql`
  mutation ToggleAIEnabledForOrganizationMutation($id: Int!, $isAIChatEnabled: Boolean!) {
    toggleAIEnabledForOrganization(id: $id, isAIChatEnabled: $isAIChatEnabled) {
      id
      isAIChatEnabled
      aiResidentAssistantUsageTier
      trialEndDate
    }
  }
`
export const useToggleAIEnabledForOrganizationMutation = () => {
  return useMutation<
    ToggleAIEnabledForOrganizationMutation,
    ToggleAIEnabledForOrganizationMutationVariables
  >({ mutationDocument: ToggleAIEnabledForOrganization })
}

const UpdateOrganizationTrialEndDate = gql`
  mutation UpdateOrganizationTrialEndDateMutation($input: UpdateOrganizationTrialEndDateInput!) {
    updateOrganizationTrialEndDate(input: $input) {
      id
      trialEndDate
    }
  }
`
export const useUpdateOrganizationTrialEndDateMutation = () => {
  return useMutation<
    UpdateOrganizationTrialEndDateMutation,
    UpdateOrganizationTrialEndDateMutationVariables
  >({ mutationDocument: UpdateOrganizationTrialEndDate })
}

const UpdateOrganizationAICommunityAssistantTier = gql`
  mutation UpdateOrganizationAICommunityAssistantTierMutation(
    $input: UpdateOrganizationAICommunityAssistantTierInput!
  ) {
    updateOrganizationAICommunityAssistantTier(input: $input) {
      id
      aiResidentAssistantUsageTier
    }
  }
`
export const useUpdateOrganizationAICommunityAssistantTierMutation = () => {
  return useMutation<
    UpdateOrganizationAICommunityAssistantTierMutation,
    UpdateOrganizationAICommunityAssistantTierMutationVariables
  >({ mutationDocument: UpdateOrganizationAICommunityAssistantTier })
}

const ToggleExternalCardReaderEnabledForOrganization = gql`
  mutation ToggleExternalCardReaderEnabledForOrganizationMutation(
    $id: Int!
    $isExternalCardReaderEnabled: Boolean!
  ) {
    toggleExternalCardReaderEnabledForOrganization(
      id: $id
      isExternalCardReaderEnabled: $isExternalCardReaderEnabled
    ) {
      id
    }
  }
`
export const useToggleExternalCardReaderEnabledForOrganizationMutation = () => {
  return useMutation<
    ToggleExternalCardReaderEnabledForOrganizationMutation,
    ToggleExternalCardReaderEnabledForOrganizationMutationVariables
  >({ mutationDocument: ToggleExternalCardReaderEnabledForOrganization })
}

const ToggleShouldShowPropertyOwnerOnInvoiceReceipt = gql`
  mutation ToggleShouldShowPropertyOwnerOnInvoiceReceiptMutation(
    $id: Int!
    $shouldShowPropertyOwnerOnInvoiceReceipt: Boolean!
  ) {
    toggleShouldShowPropertyOwnerOnInvoiceReceipt(
      id: $id
      shouldShowPropertyOwnerOnInvoiceReceipt: $shouldShowPropertyOwnerOnInvoiceReceipt
    ) {
      id
    }
  }
`
export const useToggleShouldShowPropertyOwnerOnInvoiceReceiptMutation = () => {
  return useMutation<
    ToggleShouldShowPropertyOwnerOnInvoiceReceiptMutation,
    ToggleShouldShowPropertyOwnerOnInvoiceReceiptMutationVariables
  >({ mutationDocument: ToggleShouldShowPropertyOwnerOnInvoiceReceipt })
}

const GeneratePaymentusToken = gql`
  mutation GeneratePaymentusTokenMutation($input: GeneratePaymentusTokenInput!) {
    generatePaymentusToken(input: $input) {
      token
    }
  }
`
export const useGeneratePaymentusTokenMutation = () => {
  return useMutation<GeneratePaymentusTokenMutation, GeneratePaymentusTokenMutationVariables>({
    mutationDocument: GeneratePaymentusToken,
  })
}

const GetOrganizationAIDashboardSettings = gql`
  query GetOrganizationAISettingsQuery($organizationId: Int!) {
    organization(id: $organizationId) {
      ...OrganizationAISettingsFragment
    }
    unansweredAIChatbotFAQsCount
  }
  fragment OrganizationAISettingsFragment on Organization {
    id
    aiChatbotNoSubstantiveAnswerMessage
    aiCommunityAssistantDisclaimer
    isAIChatEnabled
  }
`
export const useGetOrganizationAIDashboardSettingsQuery = () => {
  const organizationId = useMyOrgId()
  return useQuery<GetOrganizationAISettingsQuery, { organizationId: number }>({
    queryKey: [QueryKey.OrganizationAISettings, organizationId],
    queryDocument: GetOrganizationAIDashboardSettings,
    variables: { organizationId: organizationId ?? -1 },
    enabled: !!organizationId,
  })
}

const ToggleOrganizationGISSetup = gql`
  mutation ToggleOrganizationGISSetupMutation($id: Int!, $gisSetup: OrganizationGISSetup!) {
    toggleOrganizationGISSetup(id: $id, gisSetup: $gisSetup) {
      id
    }
  }
`
export const useToggleOrganizationGISSetupMutation = () => {
  return useMutation<
    ToggleOrganizationGISSetupMutation,
    ToggleOrganizationGISSetupMutationVariables
  >({
    mutationDocument: ToggleOrganizationGISSetup,
  })
}

const GisSetup = gql`
  query GisSetupQuery($organizationId: Int!) {
    organization(id: $organizationId) {
      gisSetup
      isPublicGISSearchEnabled

      entityLayers {
        ...GisSetupEntityLayerFragment
      }
      recordSettings {
        entityFieldTemplateVisibilityOnRecordSettings {
          ...EntityFieldTemplateVisibilityOnRecordSettingsFragment
        }
      }
    }
  }
  fragment GisSetupEntityLayerFragment on EntityLayer {
    id
    uuid
    name
    type
    entityFieldTemplates {
      ...EntityFieldTemplateFragment
    }
  }
  fragment EntityFieldTemplateVisibilityOnRecordSettingsFragment on EntityFieldTemplateVisibilityOnRecordSettings {
    id
    entityFieldTemplate {
      id
    }
    isVisibleOnDocuments
    isVisibleToPublic
    isVisibleToStaff
    isSearchable
  }
  fragment EntityFieldTemplateFragment on EntityFieldTemplate {
    id
    label
    isPropertyOwner
    isPropertyOwnerAddress
    entityLayer {
      id
    }
  }
`
export const useGisSetupQuery = () => {
  const organizationId = useMyOrgId()
  const { data, ...rest } = useQuery<GisSetupQuery, { organizationId: number }>({
    queryKey: [QueryKey.OrganizationGisSetup, organizationId],
    queryDocument: GisSetup,
    variables: { organizationId: organizationId ?? -1 },
    enabled: !!organizationId,
  })

  const gisSetup = useMemo(() => data?.organization?.gisSetup, [data])
  const isPublicGISSearchEnabled = useMemo(
    () => data?.organization?.isPublicGISSearchEnabled,
    [data]
  )
  const entityLayers = useMemo(() => data?.organization?.entityLayers || [], [data])
  const entityFieldTemplateVisibilityOnRecordSettings = useMemo(
    () => data?.organization?.recordSettings?.entityFieldTemplateVisibilityOnRecordSettings || [],
    [data]
  )
  const entityFieldTemplates = useMemo(
    () =>
      (data?.organization?.entityLayers || []).flatMap((el) => el.entityFieldTemplates || []) || [],
    [data]
  )

  return {
    ...rest,
    gisSetup,
    isPublicGISSearchEnabled,
    entityLayers,
    entityFieldTemplateVisibilityOnRecordSettings,
    entityFieldTemplates,
  }
}

const GetOrganizationEntityLayers = gql`
  query GetOrganizationEntityLayersQuery($organizationId: Int!) {
    organization(id: $organizationId) {
      entityLayers {
        id
        name
      }
    }
  }
`

export const useGetOrganizationEntityLayersQuery = () => {
  const organizationId = useMyOrgId()
  const { data, ...rest } = useQuery<GetOrganizationEntityLayersQuery, { organizationId: number }>({
    queryKey: [QueryKey.OrganizationEntityLayers, organizationId],
    queryDocument: GetOrganizationEntityLayers,
    variables: { organizationId: organizationId ?? -1 },
    enabled: !!organizationId,
  })
  const entityLayers = useMemo(() => data?.organization?.entityLayers || [], [data])
  return {
    data: entityLayers,
    ...rest,
  }
}

const ToggleAreAddressesAndParcelsPagesEnabled = gql`
  mutation ToggleAreAddressesAndParcelsPagesEnabledMutation(
    $id: Int!
    $areAddressesAndParcelsPagesEnabled: Boolean!
  ) {
    toggleAreAddressesAndParcelsPagesEnabled(
      id: $id
      areAddressesAndParcelsPagesEnabled: $areAddressesAndParcelsPagesEnabled
    ) {
      id
    }
  }
`
export const useToggleAreAddressesAndParcelsPagesEnabledMutation = () => {
  return useMutation<
    ToggleAreAddressesAndParcelsPagesEnabledMutation,
    ToggleAreAddressesAndParcelsPagesEnabledMutationVariables
  >({ mutationDocument: ToggleAreAddressesAndParcelsPagesEnabled })
}

const GetOrganizationForAIChatWindow = gql`
  query GetOrganizationForAIChatWindowQuery($slug: String!) {
    organizationBySlug(slug: $slug) {
      id
      aiChatIconUrl
      city
      displayName
      isAIChatEnabled
    }
  }
`
export const useGetOrganizationForAIChatWindowQuery = (slug: string) => {
  const { data, ...rest } = useQuery<
    GetOrganizationForAIChatWindowQuery,
    GetOrganizationForAIChatWindowQueryVariables
  >({
    queryKey: [QueryKey.OrganizationForAIChatWindow, slug],
    queryDocument: GetOrganizationForAIChatWindow,
    variables: { slug },
  })
  const organization = useMemo(() => data?.organizationBySlug, [data])
  return { organization, ...rest }
}

const GetOrganizationForInsightsInspections = gql`
  query GetOrganizationForInsightsInspectionsQuery($organizationId: Int!) {
    organization(id: $organizationId) {
      ...InsightsInspectionsOrganizationFragment
    }
  }
`

export const useGetOrganizationForInsightsInspections = () => {
  const organizationId = useMyOrgId()
  const { data, ...rest } = useQuery<
    GetOrganizationForInsightsInspectionsQuery,
    { organizationId: number }
  >({
    queryKey: [QueryKey.OrganizationForInsightsInspections, organizationId],
    queryDocument: GetOrganizationForInsightsInspections,
    variables: { organizationId: organizationId ?? -1 },
    enabled: !!organizationId,
  })

  return { organization: data?.organization, ...rest }
}

const UpdateAddressesAndParcelsPageHeaderText = gql`
  mutation UpdateAddressesAndParcelsPageHeaderTextMutation(
    $id: Int!
    $addressesAndParcelsPageHeaderText: String!
  ) {
    updateAddressesAndParcelsPageHeaderText(
      id: $id
      addressesAndParcelsPageHeaderText: $addressesAndParcelsPageHeaderText
    ) {
      id
    }
  }
`
export const useUpdateAddressesAndParcelsPageHeaderTextMutation = () => {
  return useMutation<
    UpdateAddressesAndParcelsPageHeaderTextMutation,
    UpdateAddressesAndParcelsPageHeaderTextMutationVariables
  >({
    mutationDocument: UpdateAddressesAndParcelsPageHeaderText,
  })
}

const OrganizationForAddressesAndParcelsPage = gql`
  query OrganizationForAddressesAndParcelsPageQuery($slug: String!) {
    organizationBySlug(slug: $slug) {
      id
      addressesAndParcelsPageHeaderText
      areAddressesAndParcelsPagesEnabled
    }
  }
`
export const useOrganizationForAddressesAndParcelsPageQuery = (slug: string) => {
  const { data, ...rest } = useQuery<
    OrganizationForAddressesAndParcelsPageQuery,
    OrganizationForAddressesAndParcelsPageQueryVariables
  >({
    queryKey: [QueryKey.OrganizationForAddressesAndParcelsPage, slug],
    queryDocument: OrganizationForAddressesAndParcelsPage,
    variables: { slug },
  })
  const organization = useMemo(() => data?.organizationBySlug, [data])
  return {
    organization,
    ...rest,
  }
}

const GetOrganizationBySlug = gql`
  query GetOrganizationBySlugQuery($slug: String, $viewportWidth: Float!) {
    organizationBySlug(slug: $slug) {
      ...OrganizationByPathFragment
    }
  }
`
export const useOrganizationBySlugQuery = (args: {
  slug: string | undefined
  viewportWidth: number
}) => {
  const { data, ...rest } = useQuery<
    GetOrganizationBySlugQuery,
    GetOrganizationBySlugQueryVariables
  >({
    queryKey: [QueryKey.OrganizationBySlug, args],
    queryDocument: GetOrganizationBySlug,
    variables: args,
    enabled: !!args.slug,
  })

  return {
    organizationBySlug: data?.organizationBySlug,
    ...rest,
  }
}

const GetOrganizationByRecordTypeId = gql`
  query GetOrganizationByRecordTypeIdQuery($recordTypeId: Int!, $viewportWidth: Float!) {
    recordTypeForApply(id: $recordTypeId) {
      id
      organization {
        ...OrganizationByPathFragment
      }
    }
  }
`
export const useOrganizationByRecordTypeIdQuery = (
  recordTypeId: number | undefined,
  viewportWidth: number,
  options?: UseQueryOptions<
    GetOrganizationByRecordTypeIdQuery,
    GetOrganizationByRecordTypeIdQueryVariables
  >
) => {
  const { data, ...rest } = useQuery<
    GetOrganizationByRecordTypeIdQuery,
    GetOrganizationByRecordTypeIdQueryVariables
  >({
    queryKey: [QueryKey.OrganizationByRecordTypeId, recordTypeId],
    queryDocument: GetOrganizationByRecordTypeId,
    variables: {
      recordTypeId: recordTypeId ?? 0,
      viewportWidth,
    },
    ...options,
  })

  return {
    organization: data?.recordTypeForApply?.organization,
    ...rest,
  }
}

const GetOrganizationByRecordTemplateUuid = gql`
  query GetOrganizationByRecordTemplateUuidQuery(
    $recordTemplateUuid: String!
    $viewportWidth: Float!
  ) {
    recordTemplate(uuid: $recordTemplateUuid) {
      id
      recordType {
        organization {
          ...OrganizationByPathFragment
        }
      }
    }
  }
`
export const useOrganizationByRecordTemplateUuidQuery = (
  recordTemplateUuid: string | undefined,
  options?: {
    viewportWidth?: number
  }
) => {
  const viewportWidth =
    options?.viewportWidth ??
    (typeof window !== 'undefined'
      ? document.body.offsetWidth || Number.MAX_SAFE_INTEGER
      : Number.MAX_SAFE_INTEGER)
  const { data, ...rest } = useQuery<
    GetOrganizationByRecordTemplateUuidQuery,
    GetOrganizationByRecordTemplateUuidQueryVariables
  >({
    queryKey: [QueryKey.OrganizationByRecordTemplateUuid, recordTemplateUuid],
    queryDocument: GetOrganizationByRecordTemplateUuid,
    variables: {
      recordTemplateUuid: recordTemplateUuid ?? '',
      viewportWidth,
    },
    enabled: !!recordTemplateUuid,
  })

  return {
    organization: data?.recordTemplate?.recordType?.organization,
    ...rest,
  }
}

const GetOrganizationByRecordUuid = gql`
  query GetOrganizationByRecordUuidQuery($recordUuid: String!, $viewportWidth: Float!) {
    recordByUuid(uuid: $recordUuid) {
      id
      organization {
        ...OrganizationByPathFragment
      }
    }
  }
`
export const useOrganizationByRecordUuidQuery = (
  recordUuid: string | undefined,
  viewportWidth: number,
  options?: UseQueryOptions<
    GetOrganizationByRecordUuidQuery,
    GetOrganizationByRecordUuidQueryVariables
  >
) => {
  const { data, ...rest } = useQuery<
    GetOrganizationByRecordUuidQuery,
    GetOrganizationByRecordUuidQueryVariables
  >({
    queryKey: [QueryKey.OrganizationByRecordUuid, recordUuid],
    queryDocument: GetOrganizationByRecordUuid,
    variables: {
      recordUuid: recordUuid ?? '',
      viewportWidth,
    },
    ...options,
  })

  return {
    organization: data?.recordByUuid?.organization,
    ...rest,
  }
}

const UpdateAIChatbotNoSubstantiveAnswerMessage = gql`
  mutation UpdateAIChatbotNoSubstantiveAnswerMessageMutation(
    $aiChatbotNoSubstantiveAnswerMessage: String!
    $organizationId: Int
  ) {
    updateAIChatbotNoSubstantiveAnswerMessage(
      aiChatbotNoSubstantiveAnswerMessage: $aiChatbotNoSubstantiveAnswerMessage
      organizationId: $organizationId
    ) {
      id
    }
  }
`
export const useUpdateAIChatbotNoSubstantiveAnswerMessageMutation = () => {
  return useMutation<
    UpdateAIChatbotNoSubstantiveAnswerMessageMutation,
    UpdateAIChatbotNoSubstantiveAnswerMessageMutationVariables
  >({
    mutationDocument: UpdateAIChatbotNoSubstantiveAnswerMessage,
  })
}

const UpdateOrganizationContactInfo = gql`
  mutation UpdateOrganizationContactInfoMutation(
    $id: Int!
    $input: UpdateOrganizationContactInfoInput!
  ) {
    updateOrganizationContactInfo(id: $id, input: $input) {
      id
    }
  }
`
export const useUpdateOrganizationContactInfoMutation = () => {
  return useMutation<
    UpdateOrganizationContactInfoMutation,
    UpdateOrganizationContactInfoMutationVariables
  >({
    mutationDocument: UpdateOrganizationContactInfo,
  })
}
